import { useEffect, useMemo, useRef, useState } from 'react';
import type { Ref } from 'react';
import { GOOGLE_MAPS_API_KEY } from '../config/env';

type AddressValue = {
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  formattedAddress?: string | null;
  googlePlaceId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  source?: 'MANUAL' | 'GOOGLE' | 'IMPORTED';
};

type AddressFieldKey = keyof Pick<AddressValue, 'line1' | 'line2' | 'city' | 'state' | 'postalCode' | 'country'>;

type AddressFieldsProps = {
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  labels?: Partial<Record<AddressFieldKey, string>>;
  errors?: Partial<Record<AddressFieldKey, string>>;
  required?: Partial<Record<AddressFieldKey, boolean>>;
  showCountry?: boolean;
  line1Ref?: Ref<HTMLInputElement>;
  className?: string;
  enableGoogleAutocomplete?: boolean;
};

type PlacePrediction = {
  placeId: string;
  description: string;
};

const PLACES_AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const PLACES_DETAILS_BASE_URL = 'https://places.googleapis.com/v1/places';

const defaultRequired: Partial<Record<AddressFieldKey, boolean>> = {
  line1: true,
  city: true,
  state: true,
  postalCode: true,
  country: true,
};

function setRef<T>(ref: Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  (ref as { current: T | null }).current = value;
}

function createSessionToken() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

async function fetchPlacePredictions(
  apiKey: string,
  input: string,
  sessionToken: string,
  signal: AbortSignal,
): Promise<PlacePrediction[]> {
  const res = await fetch(PLACES_AUTOCOMPLETE_URL, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask':
        'suggestions.placePrediction.placeId,suggestions.placePrediction.text.text,suggestions.placePrediction.structuredFormat.mainText.text,suggestions.placePrediction.structuredFormat.secondaryText.text',
    },
    body: JSON.stringify({
      input,
      sessionToken,
      languageCode: 'en',
    }),
  });
  if (!res.ok) {
    throw new Error(`Place autocomplete failed with status ${res.status}`);
  }

  const payload = (await res.json().catch(() => ({}))) as any;
  const suggestions = Array.isArray(payload?.suggestions) ? payload.suggestions : [];
  const parsed: PlacePrediction[] = [];

  for (const suggestion of suggestions) {
    const prediction = suggestion?.placePrediction;
    const placeId = typeof prediction?.placeId === 'string' ? prediction.placeId : '';
    if (!placeId) continue;
    const directText = typeof prediction?.text?.text === 'string' ? prediction.text.text : '';
    const mainText = typeof prediction?.structuredFormat?.mainText?.text === 'string' ? prediction.structuredFormat.mainText.text : '';
    const secondaryText =
      typeof prediction?.structuredFormat?.secondaryText?.text === 'string' ? prediction.structuredFormat.secondaryText.text : '';
    const description = directText || [mainText, secondaryText].filter(Boolean).join(', ') || placeId;
    parsed.push({ placeId, description });
  }

  const seen = new Set<string>();
  return parsed.filter((entry) => {
    if (seen.has(entry.placeId)) return false;
    seen.add(entry.placeId);
    return true;
  });
}

async function fetchPlaceDetails(apiKey: string, placeId: string, signal: AbortSignal) {
  const url = `${PLACES_DETAILS_BASE_URL}/${encodeURIComponent(placeId)}?languageCode=en&regionCode=US`;
  const res = await fetch(url, {
    method: 'GET',
    signal,
    headers: {
      'X-Goog-Api-Key': apiKey,
      'X-Goog-FieldMask': 'id,formattedAddress,addressComponents,location',
    },
  });
  if (!res.ok) {
    throw new Error(`Place details failed with status ${res.status}`);
  }
  return (await res.json().catch(() => ({}))) as any;
}

function parsePlaceDetails(place: any): Partial<AddressValue> {
  const components = Array.isArray(place?.addressComponents) ? place.addressComponents : [];
  const getComponent = (...types: string[]) =>
    components.find((component) => Array.isArray(component.types) && types.some((type) => component.types.includes(type)));

  const streetNumber = getComponent('street_number')?.longText || '';
  const route = getComponent('route')?.longText || '';
  const subpremise = getComponent('subpremise')?.longText || '';
  const locality =
    getComponent('locality')?.longText ||
    getComponent('postal_town')?.longText ||
    getComponent('sublocality')?.longText ||
    getComponent('administrative_area_level_2')?.longText ||
    '';
  const state =
    getComponent('administrative_area_level_1')?.shortText || getComponent('administrative_area_level_1')?.longText || '';
  const postalCode = getComponent('postal_code')?.longText || '';
  const country = getComponent('country')?.shortText || getComponent('country')?.longText || '';
  const line1 = [streetNumber, route].filter(Boolean).join(' ').trim();
  const line2 = subpremise ? `Unit ${subpremise}` : '';

  return {
    line1,
    line2,
    city: locality,
    state,
    postalCode,
    country: country || 'US',
    formattedAddress: place?.formattedAddress ?? null,
    googlePlaceId: place?.id ?? null,
    latitude: typeof place?.location?.latitude === 'number' ? Number(place.location.latitude) : null,
    longitude: typeof place?.location?.longitude === 'number' ? Number(place.location.longitude) : null,
    source: 'GOOGLE',
  };
}

function AddressInput({
  label,
  value,
  onChange,
  error,
  required,
  autoComplete,
  inputRef,
  placeholder,
  onFocus,
  onBlur,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  autoComplete?: string;
  inputRef?: Ref<HTMLInputElement>;
  placeholder?: string;
  onFocus?: () => void;
  onBlur?: () => void;
}) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-500 font-medium flex items-center gap-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      <input
        ref={inputRef}
        className={`w-full border rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-ink/10 focus:border-ink transition-all outline-none ${
          error ? 'border-rose-300' : 'border-slate-200'
        }`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </label>
  );
}

export function AddressFields({
  value,
  onChange,
  labels,
  errors,
  required = defaultRequired,
  showCountry = true,
  line1Ref,
  className = 'grid grid-cols-1 md:grid-cols-2 gap-4',
  enableGoogleAutocomplete = true,
}: AddressFieldsProps) {
  const internalLine1Ref = useRef<HTMLInputElement | null>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const sessionTokenRef = useRef(createSessionToken());
  const suppressNextSearchRef = useRef(false);
  const hideSuggestionsTimerRef = useRef<number | null>(null);
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const resolvedLine1Ref = useMemo(() => {
    return (node: HTMLInputElement | null) => {
      internalLine1Ref.current = node;
      setRef(line1Ref, node);
    };
  }, [line1Ref]);

  useEffect(() => {
    if (!enableGoogleAutocomplete || !GOOGLE_MAPS_API_KEY) {
      setSuggestions([]);
      setLoadingSuggestions(false);
      return;
    }

    const query = value.line1.trim();
    if (suppressNextSearchRef.current) {
      suppressNextSearchRef.current = false;
      return;
    }
    if (query.length < 3) {
      setSuggestions([]);
      setLoadingSuggestions(false);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setLoadingSuggestions(true);
      fetchPlacePredictions(GOOGLE_MAPS_API_KEY, query, sessionTokenRef.current, controller.signal)
        .then((entries) => {
          setSuggestions(entries);
        })
        .catch(() => {
          setSuggestions([]);
        })
        .finally(() => {
          setLoadingSuggestions(false);
        });
    }, 250);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [enableGoogleAutocomplete, value.line1]);

  useEffect(() => {
    return () => {
      if (hideSuggestionsTimerRef.current !== null) {
        window.clearTimeout(hideSuggestionsTimerRef.current);
      }
    };
  }, []);

  const showAutocompleteList =
    enableGoogleAutocomplete &&
    Boolean(GOOGLE_MAPS_API_KEY) &&
    showSuggestions &&
    (loadingSuggestions || suggestions.length > 0);

  const selectSuggestion = async (suggestion: PlacePrediction) => {
    if (!enableGoogleAutocomplete || !GOOGLE_MAPS_API_KEY) return;
    suppressNextSearchRef.current = true;
    setSuggestions([]);
    setShowSuggestions(false);

    const current = valueRef.current;
    onChangeRef.current({
      ...current,
      line1: suggestion.description,
      source: 'GOOGLE',
      googlePlaceId: suggestion.placeId,
    });

    const controller = new AbortController();
    try {
      const details = await fetchPlaceDetails(GOOGLE_MAPS_API_KEY, suggestion.placeId, controller.signal);
      const parsed = parsePlaceDetails(details);
      const next = valueRef.current;
      onChangeRef.current({
        ...next,
        ...parsed,
        line1: parsed.line1 || next.line1,
        line2: parsed.line2 || next.line2,
        city: parsed.city || next.city,
        state: parsed.state || next.state,
        postalCode: parsed.postalCode || next.postalCode,
        country: parsed.country || next.country,
        source: parsed.source || next.source,
      });
    } catch {
      // Silent fallback to the selected description.
    } finally {
      sessionTokenRef.current = createSessionToken();
    }
  };

  return (
    <div className={className}>
      <div className="md:col-span-2 relative">
        <AddressInput
          label={labels?.line1 ?? 'Address Line 1'}
          value={value.line1}
          onChange={(line1) => {
            onChange({ ...value, line1 });
            if (enableGoogleAutocomplete && GOOGLE_MAPS_API_KEY) {
              setShowSuggestions(true);
            }
          }}
          error={errors?.line1}
          required={required.line1}
          autoComplete="address-line1"
          inputRef={resolvedLine1Ref}
          placeholder={enableGoogleAutocomplete ? 'Start typing to search addresses' : undefined}
          onFocus={() => {
            if (hideSuggestionsTimerRef.current !== null) {
              window.clearTimeout(hideSuggestionsTimerRef.current);
              hideSuggestionsTimerRef.current = null;
            }
            if (enableGoogleAutocomplete && GOOGLE_MAPS_API_KEY) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => {
            hideSuggestionsTimerRef.current = window.setTimeout(() => {
              setShowSuggestions(false);
            }, 120);
          }}
        />
        {showAutocompleteList ? (
          <div className="absolute z-30 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
            {loadingSuggestions ? (
              <div className="px-3 py-2 text-xs text-slate-500">Searching addresses...</div>
            ) : (
              <ul className="max-h-64 overflow-auto py-1">
                {suggestions.map((entry) => (
                  <li key={entry.placeId}>
                    <button
                      type="button"
                      className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => void selectSuggestion(entry)}
                    >
                      {entry.description}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </div>
      <div className="md:col-span-2">
        <AddressInput
          label={labels?.line2 ?? 'Address Line 2'}
          value={value.line2}
          onChange={(line2) => onChange({ ...value, line2 })}
          error={errors?.line2}
          required={required.line2}
          autoComplete="address-line2"
        />
      </div>
      <AddressInput
        label={labels?.city ?? 'City'}
        value={value.city}
        onChange={(city) => onChange({ ...value, city })}
        error={errors?.city}
        required={required.city}
        autoComplete="address-level2"
      />
      <AddressInput
        label={labels?.state ?? 'State / Province'}
        value={value.state}
        onChange={(state) => onChange({ ...value, state })}
        error={errors?.state}
        required={required.state}
        autoComplete="address-level1"
      />
      <AddressInput
        label={labels?.postalCode ?? 'Postal Code'}
        value={value.postalCode}
        onChange={(postalCode) => onChange({ ...value, postalCode })}
        error={errors?.postalCode}
        required={required.postalCode}
        autoComplete="postal-code"
      />
      {showCountry ? (
        <AddressInput
          label={labels?.country ?? 'Country'}
          value={value.country}
          onChange={(country) => onChange({ ...value, country })}
          error={errors?.country}
          required={required.country}
          autoComplete="country-name"
        />
      ) : null}
    </div>
  );
}

export type { AddressValue };
